import type { ReactNode } from 'react';
import type { PublicSnapshot } from '../../core/types.ts';
import { compact, pct, toneClass, usd, usdSigned } from '../lib/format.ts';
import { useFlash } from '../lib/useSnapshot.ts';
import { Skeleton } from './ui.tsx';

function Stat({
  label,
  value,
  sub,
  tone = '',
  flashOn,
  accent,
}: {
  label: string;
  value: string;
  sub?: ReactNode;
  tone?: string;
  flashOn?: number;
  accent?: string;
}) {
  const flash = useFlash(flashOn);
  return (
    <div className="panel px-4 py-3">
      <p className="text-[11px] font-medium tracking-wide text-dim uppercase">{label}</p>
      <p className={`num mt-1 text-2xl leading-tight font-semibold ${tone} ${flash}`}>{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-dim">{sub}</p>}
      {accent && <div className={`mt-2 h-0.5 rounded-full ${accent}`} />}
    </div>
  );
}

export function CombinedStats({ snap }: { snap: PublicSnapshot | null }) {
  if (!snap) {
    return (
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-[92px]" />
        ))}
      </div>
    );
  }

  const c = snap.combined;
  const totalTokensBurned = snap.funds.short.lifetime.tokensBurned + snap.funds.long.lifetime.tokensBurned;

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
      <Stat
        label="Assets under management"
        value={usd(c.equityUsd)}
        flashOn={c.equityUsd}
        sub={
          <>
            short {usd(snap.funds.short.equityUsd, 0)} · long {usd(snap.funds.long.equityUsd, 0)}
          </>
        }
      />
      <Stat
        label="Open P&L"
        value={usdSigned(c.unrealizedPnlUsd)}
        tone={toneClass(c.unrealizedPnlUsd)}
        flashOn={c.unrealizedPnlUsd}
        sub={`${c.openPositions} open position${c.openPositions === 1 ? '' : 's'}`}
      />
      <Stat
        label="Realized P&L"
        value={usdSigned(c.realizedPnlUsd)}
        tone={toneClass(c.realizedPnlUsd)}
        flashOn={c.realizedPnlUsd}
        sub={
          c.winRate === null ? (
            'no closed trades yet'
          ) : (
            <>
              {pct(c.winRate, 1)} win rate · {c.wins}W / {c.losses}L
            </>
          )
        }
      />
      <Stat
        label="Burned forever"
        value={usd(c.burnedUsd)}
        tone="text-burn"
        flashOn={c.burnedUsd}
        sub={totalTokensBurned > 0 ? `${compact(totalTokensBurned)} tokens destroyed` : 'awaiting first win'}
        accent="bg-burn/40"
      />
      <Stat
        label="Creator fees collected"
        value={usd(c.creatorFeesClaimedUsd)}
        flashOn={c.creatorFeesClaimedUsd}
        sub={
          <>
            borrowing <span className={toneClass(c.fundingUsd)}>{usdSigned(c.fundingUsd)}</span>
          </>
        }
      />
    </div>
  );
}
